import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from './Button';
import { Icon } from './icons/Icon';
import { ChevronRightIcon } from './icons/ChevronRightIcon';

export interface BackButtonProps {
  /** Additional CSS classes applied to the wrapping div */
  className?: string;
}

/** Derives the parent route from the current URL path + optional location state. */
function getBackTarget(pathname: string, state: unknown): string {
  // /org/new → home
  if (pathname === '/org/new') return '/';

  // Strip /org/:orgSlug prefix and track it for reassembly.
  const orgMatch = pathname.match(/^(\/org\/[^/]+)(\/.*)?$/);
  const prefix = orgMatch ? orgMatch[1] : '';
  const local = orgMatch ? (orgMatch[2] ?? '/') : pathname;

  // /customers/:id/edit → /customers/:id
  const editMatch = local.match(/^(\/customers\/[^/]+)\/edit$/);
  if (editMatch) return `${prefix}${editMatch[1]}`;

  // /customers/:id/logs → /customers/:id
  const logsMatch = local.match(/^(\/customers\/[^/]+)\/logs$/);
  if (logsMatch) return `${prefix}${logsMatch[1]}`;

  // /customers/new → /customers
  if (local === '/customers/new') return `${prefix}/customers`;

  // /customers/:id → honour state.from (e.g. /crop-status), else /customers
  if (/^\/customers\/[^/]+$/.test(local)) {
    const from = (state as { from?: string } | null)?.from;
    return from ?? `${prefix}/customers`;
  }

  // /roles → /users
  if (local === '/roles') return `${prefix}/users`;

  // everything else (list pages, settings pages) → org dashboard (or global home)
  return prefix || '/';
}

/**
 * Navigates up the app hierarchy based on the current route.
 * Never uses browser history so it is unaffected by in-app redirects after saves.
 */
export function BackButton({ className = '' }: BackButtonProps) {
  const navigate = useNavigate();
  const { pathname, state } = useLocation();
  const { t } = useTranslation();

  function handleClick() {
    navigate(getBackTarget(pathname, state));
  }

  return (
    <div className={`mb-5 ${className}`}>
      <Button
        variant="ghost"
        size="sm"
        icon={<Icon size="xs" className="rotate-180 rtl:rotate-0"><ChevronRightIcon /></Icon>}
        onClick={handleClick}
      >
        {t('common.back')}
      </Button>
    </div>
  );
}
